import { DefaultError, MutationFunction, UseMutationOptions, useMutation } from '@tanstack/react-query';

interface UsePollingArgs {
  throwAfterMaxRetries?: boolean;
  retryOnError?: boolean;
  maxRetries?: number;
  interval?: number;
}

interface PollOptions<TData> {
  pollUntil: (res?: TData) => boolean;
}

type VariablesWithPollOptions<TData, TVariables = void> = {
  variables: TVariables;
} & PollOptions<TData>;

export const usePolling = <TData, TError = DefaultError, TVariables = void>(
  fn: MutationFunction<TData, TVariables>,
  mutationOptions?: Partial<UseMutationOptions<TData | undefined, TError, VariablesWithPollOptions<TData, TVariables>>>,
  { interval = 500, maxRetries = 25, retryOnError = false, throwAfterMaxRetries = false }: UsePollingArgs = {}
) => {
  return useMutation<TData | undefined, TError, VariablesWithPollOptions<TData, TVariables>>({
    ...mutationOptions,
    mutationFn: async ({ variables, ...args }) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          const res = await fn(variables);
          const shouldStop = args.pollUntil(res);
          if (shouldStop) {
            return res;
          }
        } catch (e) {
          if (!retryOnError) {
            throw e;
          }
        } finally {
          await new Promise(resolve => setTimeout(resolve, interval));
        }
      }

      if (throwAfterMaxRetries) {
        throw new Error(`poll timed out after ${maxRetries} retries, poll condition was not fulfilled`);
      }

      return;
    },
  });
};
